import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { testDataService, type TestDataEntityType } from '@/services/platform/test-data';

/**
 * Shared "mark as test" behaviour for the original screens (order /
 * customer row menus, detail views). Gated by the same `test-data-tools`
 * feature flag the backend enforces — when the tenant lacks it, `enabled`
 * is false and callers hide the action.
 */
export function useMarkAsTest(entityType: TestDataEntityType) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const enabled = (me?.featureFlags ?? []).includes('test-data-tools');

  const markMut = useMutation({
    mutationFn: (ids: string | string[]) =>
      testDataService.mark(entityType, Array.isArray(ids) ? ids : [ids]),
    onSuccess: (r) => {
      toast.success(`Marked ${r.marked} as test`);
      qc.invalidateQueries({ queryKey: ['test-data'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unmarkMut = useMutation({
    mutationFn: (ids: string | string[]) =>
      testDataService.unmark(entityType, Array.isArray(ids) ? ids : [ids]),
    onSuccess: () => {
      toast.success('Test mark removed');
      qc.invalidateQueries({ queryKey: ['test-data'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    enabled,
    mark: markMut.mutate,
    unmark: unmarkMut.mutate,
    isPending: markMut.isPending || unmarkMut.isPending,
  };
}
